import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  apiCreateTicket,
  apiDownloadMyData,
  apiGetMe,
  apiGrantHealthConsent,
  apiUpdateMe,
  apiUploadAvatar,
  apiGetSuivisForSportive,
  apiGetSuivisForCoach,
  apiGetTeams,
  apiWithdrawHealthConsent,
  BASE_URL,
} from '../services/api';
import { ReactComponent as EditIcon } from '../assets/profile/Edit.svg';
import { ReactComponent as PhotoIcon } from '../assets/profile/Photo.svg';
import { ReactComponent as ShareyIcon } from '../assets/profile/Sharey.svg';
import { ReactComponent as NotifIcon } from '../assets/profile/Notif_b.svg';
import { ReactComponent as FileIcon } from '../assets/profile/File.svg';
import { ReactComponent as GotoIcon } from '../assets/profile/Goto.svg';
import { SHOW_NOTIFICATIONS, SHOW_SUBSCRIPTION } from '../constants/featureFlags';
import { SPORT_LABELS, sportEmoji } from '../constants/sports';
import { CONTRACEPTION_OPTIONS } from '../constants/contraception';
import { APP_VERSION } from '../constants/site';
import { todayISO, toISODate } from '../utils/formDate';
import {
  NAME_MAX, validateBirthDate, validateCycleDate, validateName,
} from '../utils/signupValidation';
import './Profile.css';

// Miroir de `SportLevel` (app/enums.py), comme à l'onboarding.
const SPORT_LEVELS = ['Débutante', 'Intermédiaire', 'Confirmée', 'Professionnelle'];

const Profile = () => {
  const navigate = useNavigate();
  const { user, logoutUser, getToken, updateUser } = useAuth();

  const isCoach = user?.role === 'coach';
  const firstName = user?.prenom || '—';
  const lastName = user?.nom || '—';
  const email = user?.mail || '—';
  const niveau = user?.niveau || '—';
  const club = user?.club || '—';

  const birthDate = user?.date_naissance
    ? new Date(user.date_naissance).toLocaleDateString('fr-FR')
    : '—';

  const age = user?.date_naissance
    ? Math.floor((Date.now() - new Date(user.date_naissance)) / (365.25 * 24 * 3600 * 1000))
    : null;

  // Demande de suppression de compte (RGPD Art. 17) — ouvre un ticket support
  const [deletionOpen, setDeletionOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionSending, setDeletionSending] = useState(false);
  const [deletionError, setDeletionError] = useState('');

  const closeDeletionModal = () => {
    if (deletionSending) return;
    setDeletionOpen(false);
    setDeletionReason('');
  };

  const handleRequestDeletion = async () => {
    setDeletionError('');
    setDeletionSending(true);
    try {
      const ticket = await apiCreateTicket({
        categorie: 'suppression_compte',
        sujet: 'Demande de suppression de compte',
        // Le motif est facultatif : sans lui, le message reste explicite pour
        // l'admin qui traitera la demande.
        message: deletionReason.trim()
          || 'Je demande la suppression de mon compte et de mes données.',
      }, getToken());
      setDeletionOpen(false);
      setDeletionReason('');
      navigate(`/support/ticket/${ticket.id}`);
    } catch (err) {
      setDeletionError(err.message);
    } finally {
      setDeletionSending(false);
    }
  };

  // Mes données — export (RGPD Art. 20) et consentement santé (Art. 7-3)
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleExport = async () => {
    setExportError('');
    setExporting(true);
    try {
      await apiDownloadMyData(getToken());
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const healthWithdrawn = Boolean(user?.health_consent_withdrawn_at);
  // 'withdraw' | 'grant' | null
  const [consentModal, setConsentModal] = useState(null);
  const [consentPassword, setConsentPassword] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSending, setConsentSending] = useState(false);
  const [consentError, setConsentError] = useState('');

  const openConsentModal = (kind) => {
    setConsentError('');
    setConsentPassword('');
    setConsentChecked(false);
    setConsentModal(kind);
  };

  const closeConsentModal = () => {
    if (consentSending) return;
    setConsentModal(null);
  };

  const handleConsentConfirm = async () => {
    setConsentError('');
    setConsentSending(true);
    try {
      const token = getToken();
      if (consentModal === 'withdraw') {
        await apiWithdrawHealthConsent(consentPassword, token);
      } else {
        await apiGrantHealthConsent(token);
      }
      // Le retrait vide le profil de santé et referme les partages : on relit les
      // deux plutôt que de deviner l'état côté client.
      updateUser(await apiGetMe(token));
      setSuivis(await apiGetSuivisForSportive(user.id, token).catch(() => []));
      setConsentModal(null);
    } catch (err) {
      setConsentError(err.message);
    } finally {
      setConsentSending(false);
    }
  };

  // Photo de profil
  const fileInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError('');
    try {
      const { avatar_url } = await apiUploadAvatar(file, getToken());
      updateUser({ ...user, avatar_url });
    } catch (err) {
      setAvatarError(err.message);
    } finally {
      setAvatarUploading(false);
    }
  };

  // Notifications & Rappels (persistées en base)
  const [rappel, setRappel] = useState(user?.notif_rappel ?? true);
  const [promos, setPromos] = useState(user?.notif_promos ?? false);
  const [newsletter, setNewsletter] = useState(user?.notif_newsletter ?? true);

  const toggleNotif = async (field, current, setter) => {
    const next = !current;
    setter(next);
    try {
      const updated = await apiUpdateMe({ [field]: next }, getToken());
      updateUser(updated);
    } catch (_) {
      setter(current);
    }
  };

  // Options de partage (sportive uniquement) — état réel des Suivi liés
  const [suivis, setSuivis] = useState([]);
  const [suivisLoading, setSuivisLoading] = useState(!isCoach);

  useEffect(() => {
    if (isCoach || !user?.id) return;
    apiGetSuivisForSportive(user.id, getToken())
      .then(setSuivis)
      .catch(() => {})
      .finally(() => setSuivisLoading(false));
  }, [isCoach, user?.id, getToken]);

  // Activité du coach. Son onglet Profil n'affichait que son club : ces deux
  // compteurs lui donnent enfin un contenu propre, sans toucher aux données de
  // santé de ses sportives.
  const [coachStats, setCoachStats] = useState(null);

  useEffect(() => {
    if (!isCoach || !user?.id) return;
    const token = getToken();
    Promise.all([
      apiGetSuivisForCoach(user.id, token).catch(() => []),
      apiGetTeams(token).catch(() => []),
    ]).then(([coachSuivis, teams]) => {
      setCoachStats({ sportives: coachSuivis.length, equipes: teams.length });
    });
  }, [isCoach, user?.id, getToken]);

  const hasActiveCoach = suivis.length > 0;
  const sharingSuivis = suivis.filter((s) => s.partage_cycle || s.partage_sport || s.partage_mental);
  const anyShared = sharingSuivis.length > 0;
  const anyWithDetail = sharingSuivis.some((s) => s.partage_detail);
  const shareDetailLabel = !anyShared ? 'Aucune donnée' : anyWithDetail ? 'Avec détails' : 'Sans détails';

  const [userSports, setUserSports] = useState(user?.sports_list || []);
  const [showSportPicker, setShowSportPicker] = useState(false);
  const [sportSaving, setSportSaving] = useState(false);
  const [customSport, setCustomSport] = useState('');

  const saveSports = async (list) => {
    setSportSaving(true);
    try {
      const updated = await apiUpdateMe({ sports_list: list }, getToken());
      updateUser(updated);
      setUserSports(updated.sports_list || list);
    } catch (_) {}
    finally { setSportSaving(false); }
  };

  const addSport = (s) => {
    if (userSports.includes(s)) return;
    const next = [...userSports, s];
    setShowSportPicker(false);
    saveSports(next);
  };
  const removeSport = (s) => saveSports(userSports.filter(x => x !== s));

  // Sport hors liste (Hyrox, pole dance, discipline de niche…) : même
  // traitement que les autres une fois ajouté.
  const addCustomSport = () => {
    const label = customSport.trim();
    if (!label) return;
    setCustomSport('');
    if (userSports.some(s => s.toLowerCase() === label.toLowerCase())) return;
    saveSports([...userSports, label]);
  };

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    prenom: user?.prenom || '',
    nom: user?.nom || '',
    date_naissance: user?.date_naissance || '',
    niveau: user?.niveau || '',
    sport: user?.sport || '',
    club: user?.club || '',
  });
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleEditStart = () => {
    setEditForm({
      prenom: user?.prenom || '',
      nom: user?.nom || '',
      date_naissance: user?.date_naissance || '',
      niveau: user?.niveau || '',
      sport: user?.sport || '',
      club: user?.club || '',
    });
    setEditError('');
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditError('');
  };

  const handleEditSave = async () => {
    setEditError('');
    const fieldError =
      validateName(editForm.prenom, 'Le prénom')
      || validateName(editForm.nom, 'Le nom')
      || validateBirthDate(editForm.date_naissance);
    if (fieldError) {
      setEditError(fieldError);
      return;
    }
    setSaving(true);
    try {
      const updated = await apiUpdateMe(
        {
          prenom: editForm.prenom.trim() || undefined,
          nom: editForm.nom.trim() || undefined,
          date_naissance: editForm.date_naissance || undefined,
          ...(isCoach
            ? { club: editForm.club.trim() || undefined }
            : { niveau: editForm.niveau || undefined, sport: editForm.sport || undefined }),
        },
        getToken()
      );
      updateUser(updated);
      setEditing(false);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Paramètres de cycle. Saisis à l'onboarding puis figés faute d'écran pour les
  // rouvrir, alors que l'étape d'inscription promet de pouvoir les corriger — et
  // qu'une date fausse ancre durablement les prédictions.
  // En fuseau local : `toISOString()` (UTC) décalait les bornes d'un jour la nuit.
  const todayStr = todayISO();
  const oneYearAgoDate = new Date();
  oneYearAgoDate.setDate(oneYearAgoDate.getDate() - 365);
  const oneYearAgoStr = toISODate(oneYearAgoDate);

  const [cycleEditing, setCycleEditing] = useState(false);
  const [cycleForm, setCycleForm] = useState({
    contraception: user?.contraception || '',
    derniere_regles: user?.derniere_regles || '',
    derniere_ovulation: user?.derniere_ovulation || '',
  });
  const [cycleError, setCycleError] = useState('');
  const [cycleSaving, setCycleSaving] = useState(false);

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

  const handleCycleEditStart = () => {
    setCycleForm({
      contraception: user?.contraception || '',
      derniere_regles: user?.derniere_regles || '',
      derniere_ovulation: user?.derniere_ovulation || '',
    });
    setCycleError('');
    setCycleEditing(true);
  };

  const handleCycleSave = async () => {
    setCycleError('');
    // `min`/`max` ne bornent que le sélecteur, pas une date tapée au clavier.
    const dateError =
      validateCycleDate(cycleForm.derniere_regles) || validateCycleDate(cycleForm.derniere_ovulation);
    if (dateError) {
      setCycleError(dateError);
      return;
    }
    setCycleSaving(true);
    try {
      // `null` et non `undefined` pour les dates : le backend distingue « champ absent,
      // ne touche à rien » de « champ vidé, efface-le » (PUT /users/me, exclude_unset).
      // Sans ça, une date mal saisie resterait impossible à retirer.
      const updated = await apiUpdateMe(
        {
          contraception: cycleForm.contraception || null,
          derniere_regles: cycleForm.derniere_regles || null,
          derniere_ovulation: cycleForm.derniere_ovulation || null,
        },
        getToken()
      );
      updateUser(updated);
      setCycleEditing(false);
    } catch (err) {
      setCycleError(err.message);
    } finally {
      setCycleSaving(false);
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  return (
    <div className="profile-page">
      <h1 className="profile-page__title">Profil</h1>

      {/* Avatar + infos principales */}
      <div className="profile-page__card profile-page__identity">
        <div className="profile-page__avatar-wrapper">
          <div className="profile-page__avatar-circle">
            {user?.avatar_url ? (
              <img
                src={`${BASE_URL}${user.avatar_url}`}
                alt="Photo de profil"
                className="profile-page__avatar-img"
              />
            ) : (
              <span className="profile-page__avatar-initials">
                {(user?.prenom || 'U').substring(0, 3)}
              </span>
            )}
          </div>
          <button
            type="button"
            className="profile-page__avatar-camera"
            onClick={handleAvatarClick}
            disabled={avatarUploading}
            aria-label="Changer la photo de profil"
          >
            <PhotoIcon />
          </button>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            ref={fileInputRef}
            onChange={handleAvatarChange}
            style={{ display: 'none' }}
          />
        </div>
        <h2 className="profile-page__name">{user?.prenom || firstName}</h2>
        <p className="profile-page__email">{email}</p>
        {age !== null && <p className="profile-page__age">{age} ans</p>}
        {avatarUploading && <p className="profile-page__age">Envoi de la photo…</p>}
        {avatarError && <p style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{avatarError}</p>}
      </div>

      {SHOW_SUBSCRIPTION && (
        <>
          <h3 className="profile-page__section-title">Abonnement</h3>
          <div className="profile-page__card profile-page__subscription">
            <div className="profile-page__sub-left">
              <div className="profile-page__sub-icon-wrap" />
              <div>
                <span className="profile-page__sub-plan">Pro</span>
                <span className="profile-page__sub-price">6.99€<span className="profile-page__sub-period">/mois</span> <span className="profile-page__sub-old">8.99€</span></span>
              </div>
            </div>
            <button className="profile-page__edit-btn"><EditIcon /></button>
          </div>
        </>
      )}

      {/* Informations privées */}
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Informations privées</h3>
          {!editing && (
            <button className="profile-page__edit-btn" onClick={handleEditStart}><EditIcon /></button>
          )}
        </div>
        {editing ? (
          <>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Prénom</span>
              <input
                id="edit-prenom"
                name="prenom"
                autoComplete="given-name"
                maxLength={NAME_MAX}
                className="profile-page__edit-input"
                value={editForm.prenom}
                onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })}
              />
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Nom</span>
              <input
                id="edit-nom"
                name="nom"
                autoComplete="family-name"
                maxLength={NAME_MAX}
                className="profile-page__edit-input"
                value={editForm.nom}
                onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
              />
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Email</span>
              <span className="profile-page__info-value">{email}</span>
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Date de naissance</span>
              <input
                id="edit-date-naissance"
                name="date_naissance"
                className="profile-page__edit-input"
                type="date"
                autoComplete="bday"
                max={todayStr}
                value={editForm.date_naissance}
                onChange={(e) => setEditForm({ ...editForm, date_naissance: e.target.value })}
              />
            </div>
            {isCoach ? (
              <div className="profile-page__info-row profile-page__info-row--last">
                <span className="profile-page__info-label">Club</span>
                <input
                  id="edit-club"
                  name="club"
                  autoComplete="organization"
                  maxLength={255}
                  className="profile-page__edit-input"
                  value={editForm.club}
                  onChange={(e) => setEditForm({ ...editForm, club: e.target.value })}
                />
              </div>
            ) : (
              <>
                <div className="profile-page__info-row">
                  <span className="profile-page__info-label">Niveau sportif</span>
                  {/* Liste fermée, comme à l'onboarding : en champ libre, « debutante »
                      ou « niveau 2 » cohabitaient avec les quatre niveaux prévus. */}
                  <select
                    id="edit-niveau"
                    name="niveau"
                    className="profile-page__edit-input"
                    value={editForm.niveau}
                    onChange={(e) => setEditForm({ ...editForm, niveau: e.target.value })}
                  >
                    <option value="">— choisir —</option>
                    {/* Valeur historique hors liste : conservée pour ne pas l'effacer en silence. */}
                    {editForm.niveau && !SPORT_LEVELS.includes(editForm.niveau) && (
                      <option value={editForm.niveau}>{editForm.niveau}</option>
                    )}
                    {SPORT_LEVELS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
                <div className="profile-page__info-row profile-page__info-row--last">
                  <span className="profile-page__info-label">Sport principal</span>
                  <select
                    id="edit-sport"
                    name="sport"
                    className="profile-page__edit-input"
                    value={editForm.sport}
                    onChange={(e) => setEditForm({ ...editForm, sport: e.target.value })}
                  >
                    <option value="">— choisir —</option>
                    {SPORT_LABELS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {editError && (
              <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: '8px 0 0' }}>{editError}</p>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button className="profile-page__save-btn" onClick={handleEditSave} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button className="profile-page__cancel-btn" onClick={handleEditCancel} disabled={saving}>
                Annuler
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Prénom</span>
              <span className="profile-page__info-value">{user?.prenom || firstName}</span>
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Nom</span>
              <span className="profile-page__info-value">{user?.nom || lastName}</span>
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Email</span>
              <span className="profile-page__info-value">{email}</span>
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Date de naissance</span>
              <span className="profile-page__info-value">{birthDate}</span>
            </div>
            {isCoach ? (
              <div className="profile-page__info-row profile-page__info-row--last">
                <span className="profile-page__info-label">Club</span>
                <span className="profile-page__info-value">{club}</span>
              </div>
            ) : (
              <>
                <div className="profile-page__info-row">
                  <span className="profile-page__info-label">Niveau sportif</span>
                  <span className="profile-page__info-value">{user?.niveau || niveau}</span>
                </div>
                <div className="profile-page__info-row profile-page__info-row--last">
                  <span className="profile-page__info-label">Sport principal</span>
                  <span className="profile-page__info-value">{user?.sport || '—'}</span>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Mon cycle — sportive uniquement. Séparé des informations privées : ce sont
          des données de santé, et surtout les seules dont la modification déplace les
          prédictions de la page Cycle. */}
      {!isCoach && (
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Mon cycle</h3>
          {/* Consentement santé retiré : le serveur refuse ces champs, inutile
              d'ouvrir un formulaire qui échouerait à l'enregistrement. */}
          {!cycleEditing && !healthWithdrawn && (
            <button className="profile-page__edit-btn" onClick={handleCycleEditStart}><EditIcon /></button>
          )}
        </div>
        {cycleEditing ? (
          <>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Contraception</span>
              <select
                id="edit-contraception"
                name="contraception"
                className="profile-page__edit-input"
                value={cycleForm.contraception}
                onChange={(e) => setCycleForm({ ...cycleForm, contraception: e.target.value })}
              >
                <option value="">— non renseignée —</option>
                {CONTRACEPTION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Dernières règles</span>
              <input
                id="edit-derniere-regles"
                name="derniere_regles"
                className="profile-page__edit-input"
                type="date"
                max={todayStr}
                min={oneYearAgoStr}
                value={cycleForm.derniere_regles}
                onChange={(e) => setCycleForm({ ...cycleForm, derniere_regles: e.target.value })}
              />
            </div>
            <div className="profile-page__info-row profile-page__info-row--last">
              <span className="profile-page__info-label">Dernière ovulation</span>
              <input
                id="edit-derniere-ovulation"
                name="derniere_ovulation"
                className="profile-page__edit-input"
                type="date"
                max={todayStr}
                min={oneYearAgoStr}
                value={cycleForm.derniere_ovulation}
                onChange={(e) => setCycleForm({ ...cycleForm, derniere_ovulation: e.target.value })}
              />
            </div>
            <p className="profile-page__cycle-warning">
              Ces dates servent de point de départ à tes prédictions : les corriger
              recalcule ta phase actuelle et la date de tes prochaines règles.
            </p>
            {cycleError && (
              <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: '8px 0 0' }}>{cycleError}</p>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button className="profile-page__save-btn" onClick={handleCycleSave} disabled={cycleSaving}>
                {cycleSaving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button
                className="profile-page__cancel-btn"
                onClick={() => { setCycleEditing(false); setCycleError(''); }}
                disabled={cycleSaving}
              >
                Annuler
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Contraception</span>
              <span className="profile-page__info-value">{user?.contraception || '—'}</span>
            </div>
            <div className="profile-page__info-row">
              <span className="profile-page__info-label">Dernières règles</span>
              <span className="profile-page__info-value">{formatDate(user?.derniere_regles)}</span>
            </div>
            <div className="profile-page__info-row profile-page__info-row--last">
              <span className="profile-page__info-label">Dernière ovulation</span>
              <span className="profile-page__info-value">{formatDate(user?.derniere_ovulation)}</span>
            </div>
          </>
        )}
      </div>
      )}

      {/* Mon activité — coach uniquement. Aucune donnée de santé : deux
          compteurs et les raccourcis vers les écrans qui les portent. */}
      {isCoach && (
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Mon activité</h3>
        </div>
        <div className="profile-page__info-row">
          <span className="profile-page__info-label">Sportives suivies</span>
          <span className="profile-page__info-value">
            {coachStats ? coachStats.sportives : '…'}
          </span>
        </div>
        <div className="profile-page__info-row profile-page__info-row--last">
          <span className="profile-page__info-label">Équipes</span>
          <span className="profile-page__info-value">
            {coachStats ? coachStats.equipes : '…'}
          </span>
        </div>
      </div>
      )}

      {/* Sports pratiqués — sportive uniquement */}
      {!isCoach && (
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Sports pratiqués</h3>
          {sportSaving && <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Sauvegarde…</span>}
        </div>
        <div className="profile-page__sports-chips">
          {userSports.map(s => (
            <span key={s} className="profile-page__sport-chip">
              <span aria-hidden="true">{sportEmoji(s)}</span>
              <span className="profile-page__sport-chip-label">{s}</span>
              <button
                className="profile-page__sport-chip-remove"
                onClick={() => removeSport(s)}
                aria-label={`Retirer ${s}`}
                disabled={sportSaving}
              >×</button>
            </span>
          ))}
          <button
            className="profile-page__sport-add"
            onClick={() => setShowSportPicker(p => !p)}
            disabled={sportSaving}
          >+</button>
        </div>
        {showSportPicker && (
          <>
            <div className="profile-page__sport-picker">
              {SPORT_LABELS.filter(s => !userSports.includes(s)).length === 0
                ? <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: 0 }}>Tous les sports sont ajoutés</p>
                : SPORT_LABELS.filter(s => !userSports.includes(s)).map(s => (
                    <button key={s} className="profile-page__sport-picker-item" onClick={() => addSport(s)}>
                      <span aria-hidden="true">{sportEmoji(s)}</span> {s}
                    </button>
                  ))
              }
            </div>
            <div className="profile-page__sport-custom">
              <input
                id="profile-sport-autre"
                name="sport-autre"
                type="text"
                className="profile-page__edit-input"
                placeholder="Autre sport (ex : Hyrox)"
                value={customSport}
                maxLength={40}
                onChange={(e) => setCustomSport(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomSport();
                  }
                }}
              />
              <button
                type="button"
                className="profile-page__sport-custom-add"
                onClick={addCustomSport}
                disabled={!customSport.trim() || sportSaving}
              >
                Ajouter
              </button>
            </div>
          </>
        )}
      </div>
      )}

      {/* Options de partage — sportive uniquement */}
      {!isCoach && (
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Options de partage</h3>
          <button
            className="profile-page__edit-btn"
            onClick={() => navigate('/formulaire/share')}
            disabled={!hasActiveCoach}
          >
            <ShareyIcon className="profile-page__share-icon" />
          </button>
        </div>
        <p className="profile-page__share-desc">
          Configure le partage automatique de tes données avec ton coach
        </p>
        {suivisLoading ? (
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: 0 }}>Chargement…</p>
        ) : !hasActiveCoach ? (
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: 0 }}>
            Aucun coach ne te suit actuellement.
          </p>
        ) : (
          <div
            className="profile-page__share-row"
            onClick={() => navigate('/formulaire/share')}
            style={{ cursor: 'pointer', padding: '10px 14px' }}
          >
            <span className="profile-page__share-tag">
              {anyShared ? 'Partage actif' : 'Partage inactif'}
            </span>
            <button className="profile-page__share-link">{shareDetailLabel}</button>
          </div>
        )}
      </div>
      )}

      {/* Notifications & Rappels — masqué tant que rien n'envoie de notif (SHOW_NOTIFICATIONS).
          Les préférences restent persistées en base, seul l'affichage est retiré. */}
      {SHOW_NOTIFICATIONS && (
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Notifications & Rappels</h3>
          <NotifIcon className="profile-page__notif-icon" />
        </div>
        <div className="profile-page__toggle-row">
          <span>Rappel quotidien</span>
          <div
            className={`profile-page__toggle${rappel ? ' profile-page__toggle--on' : ''}`}
            onClick={() => toggleNotif('notif_rappel', rappel, setRappel)}
            role="switch"
            aria-checked={rappel}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleNotif('notif_rappel', rappel, setRappel)}
          />
        </div>
        <div className="profile-page__toggle-row">
          <span>Promotions</span>
          <div
            className={`profile-page__toggle${promos ? ' profile-page__toggle--on' : ''}`}
            onClick={() => toggleNotif('notif_promos', promos, setPromos)}
            role="switch"
            aria-checked={promos}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleNotif('notif_promos', promos, setPromos)}
          />
        </div>
        <div className="profile-page__toggle-row">
          <span>Newsletter</span>
          <div
            className={`profile-page__toggle${newsletter ? ' profile-page__toggle--on' : ''}`}
            onClick={() => toggleNotif('notif_newsletter', newsletter, setNewsletter)}
            role="switch"
            aria-checked={newsletter}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleNotif('notif_newsletter', newsletter, setNewsletter)}
          />
        </div>
      </div>
      )}

      {/* Mes données — droits RGPD exerçables sans passer par le support :
          portabilité (Art. 20) pour tous, retrait du consentement santé (Art. 7-3)
          pour les sportives. */}
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Mes données</h3>
        </div>
        {!isCoach && (
          <div className="profile-page__info-row">
            <span className="profile-page__info-label">Données de santé</span>
            <span className="profile-page__info-value">
              {healthWithdrawn
                ? `Consentement retiré le ${formatDate(user.health_consent_withdrawn_at)}`
                : 'Consentement donné'}
            </span>
          </div>
        )}
        <button
          type="button"
          className="profile-page__data-action"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Préparation…' : 'Télécharger mes données'}
        </button>
        {exportError && <p className="profile-page__modal-error">{exportError}</p>}
        {!isCoach && (
          <button
            type="button"
            className={`profile-page__data-action${healthWithdrawn ? '' : ' profile-page__data-action--danger'}`}
            onClick={() => openConsentModal(healthWithdrawn ? 'grant' : 'withdraw')}
          >
            {healthWithdrawn
              ? 'Donner à nouveau mon consentement'
              : 'Retirer mon consentement'}
          </button>
        )}
      </div>

      {/* Légal & Support */}
      <div className="profile-page__card">
        <div className="profile-page__card-header">
          <h3>Légal & Support</h3>
        </div>
        <div className="profile-page__legal-row" onClick={() => navigate('/legal/conditions')} style={{ cursor: 'pointer' }}>
          <span className="profile-page__legal-item"><FileIcon className="profile-page__legal-file-icon" /> Conditions générales</span>
          <GotoIcon className="profile-page__legal-arrow" />
        </div>
        <div className="profile-page__legal-row" onClick={() => navigate('/legal/confidentialite')} style={{ cursor: 'pointer' }}>
          <span className="profile-page__legal-item"><FileIcon className="profile-page__legal-file-icon" /> Politique de confidentialité</span>
          <GotoIcon className="profile-page__legal-arrow" />
        </div>
        <div className="profile-page__legal-row profile-page__info-row--last" onClick={() => navigate('/legal/contact')} style={{ cursor: 'pointer' }}>
          <span className="profile-page__legal-item"><FileIcon className="profile-page__legal-file-icon" /> Contact & Support</span>
          <GotoIcon className="profile-page__legal-arrow" />
        </div>
      </div>

      <p className="profile-page__version">Version {APP_VERSION}</p>

      <button className="profile-page__logout" onClick={handleLogout}>
        Se déconnecter
      </button>

      {/* Droit à l'effacement (RGPD Art. 17). C'est une *demande* et non une
          suppression immédiate : un humain vérifie avant un geste irréversible,
          et la personne garde une trace écrite de sa démarche dans son fil. */}
      <button
        className="profile-page__delete-account"
        onClick={() => { setDeletionError(''); setDeletionOpen(true); }}
      >
        Demander la suppression de mon compte
      </button>

      {consentModal === 'withdraw' && (
        <div className="profile-page__modal-overlay" onClick={closeConsentModal}>
          <div className="profile-page__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="profile-page__modal-title">Retirer mon consentement</h3>
            <p className="profile-page__modal-text">
              CycleGala cessera de traiter tes données de santé. Dès que tu confirmes :
            </p>
            <ul className="profile-page__modal-list">
              <li>
                tes relevés de cycle, de mental et de sport, ainsi que ta taille, ton poids,
                ta contraception et tes dates de cycle sont <strong>définitivement supprimés</strong> ;
              </li>
              <li>tous tes partages avec ton coach sont coupés ;</li>
              <li>tu ne peux plus faire de nouvelle saisie.</li>
            </ul>
            <p className="profile-page__modal-text">
              Ton compte est conservé, et tu pourras redonner ton consentement plus tard — tu
              repartiras alors d'un historique vide. Pense à télécharger tes données avant si
              tu veux les garder.
            </p>

            <label className="profile-page__modal-label" htmlFor="consent-password">
              Mot de passe
            </label>
            <input
              id="consent-password"
              type="password"
              autoComplete="current-password"
              className="profile-page__modal-textarea"
              value={consentPassword}
              onChange={(e) => setConsentPassword(e.target.value)}
            />

            {consentError && <p className="profile-page__modal-error">{consentError}</p>}

            <div className="profile-page__modal-actions">
              <button
                className="profile-page__cancel-btn"
                onClick={closeConsentModal}
                disabled={consentSending}
              >
                Annuler
              </button>
              <button
                className="profile-page__modal-confirm"
                onClick={handleConsentConfirm}
                disabled={consentSending || !consentPassword}
              >
                {consentSending ? 'Suppression…' : 'Retirer et supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {consentModal === 'grant' && (
        <div className="profile-page__modal-overlay" onClick={closeConsentModal}>
          <div className="profile-page__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="profile-page__modal-title">Donner mon consentement</h3>
            <p className="profile-page__modal-text">
              Tu pourras de nouveau saisir ton cycle, ton mental et ton sport, et recevoir
              des suggestions adaptées à ta phase. Tes partages avec ton coach restent coupés :
              c'est à toi de les rouvrir si tu le souhaites.
            </p>

            <label className="profile-page__modal-check">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>
                J'accepte le traitement de mes données de santé tel que décrit dans la{' '}
                <button
                  type="button"
                  className="profile-page__modal-link"
                  onClick={() => navigate('/legal/confidentialite')}
                >
                  politique de confidentialité
                </button>
                .
              </span>
            </label>

            {consentError && <p className="profile-page__modal-error">{consentError}</p>}

            <div className="profile-page__modal-actions">
              <button
                className="profile-page__cancel-btn"
                onClick={closeConsentModal}
                disabled={consentSending}
              >
                Annuler
              </button>
              <button
                className="profile-page__save-btn"
                onClick={handleConsentConfirm}
                disabled={consentSending || !consentChecked}
              >
                {consentSending ? 'Enregistrement…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletionOpen && (
        <div className="profile-page__modal-overlay" onClick={closeDeletionModal}>
          <div className="profile-page__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="profile-page__modal-title">Supprimer mon compte</h3>
            <p className="profile-page__modal-text">
              Votre demande est enregistrée puis traitée par notre équipe dans un délai
              d'un mois (Art.&nbsp;12 RGPD). Une fois traitée, vos données personnelles
              seront effacées et vous perdrez l'accès à votre compte.
            </p>
            <p className="profile-page__modal-text">
              Vous pouvez suivre la demande, et revenir dessus, depuis
              Contact&nbsp;&amp;&nbsp;Support.
            </p>

            <label className="profile-page__modal-label" htmlFor="deletion-reason">
              Motif (facultatif)
            </label>
            <textarea
              id="deletion-reason"
              className="profile-page__modal-textarea"
              rows={3}
              maxLength={2000}
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
            />

            {deletionError && (
              <p className="profile-page__modal-error">{deletionError}</p>
            )}

            <div className="profile-page__modal-actions">
              <button
                className="profile-page__cancel-btn"
                onClick={closeDeletionModal}
                disabled={deletionSending}
              >
                Annuler
              </button>
              <button
                className="profile-page__modal-confirm"
                onClick={handleRequestDeletion}
                disabled={deletionSending}
              >
                {deletionSending ? 'Envoi…' : 'Envoyer ma demande'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
